import { type Dispatch, type SetStateAction, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { MediaImage } from './ImagesVideo/types';
import { canPersistRoomImageHiddenState, getMediaImageSourceKey, removeImageOverride } from './ImagesVideo/utils';

interface UseOfferBuilderMediaVisibilityParams {
  offerId: string;
  mediaImages: Array<MediaImage>;
  removeUploadedImage: (publicId: string) => void;
  markDeletedOfferImage: (publicId: string) => void;
  markRoomImageRemoved: (roomKey: string, publicId: string) => void;
  cleanupDeletedOfferGalleryImage: (publicId: string, deletedImageWasPending: boolean) => void;
  cleanupDeletedRoomGalleryImage: (roomKey: string, publicId: string, deletedImageWasPending: boolean) => void;
  clearImageEdit: (publicId: string) => void;
  setRoomImagePendingEdit: Dispatch<SetStateAction<MediaImage | undefined>>;
}

export interface OfferBuilderMediaVisibilityController {
  hiddenImageOverrides: Record<string, boolean>;
  setHiddenImageOverrides: (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void;
  roomImagePendingDeletion?: MediaImage;
  removeRoomImageFromEditor: (image: MediaImage) => void;
  closeRoomImageDeleteDialog: () => void;
  confirmRemoveRoomImage: () => Promise<void>;
  deleteImage: (publicId: string, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  removeImageFromRoom: (image: MediaImage, options?: { showError?: boolean; showSuccess?: boolean }) => Promise<void>;
  toggleHiddenImage: (publicId: string) => Promise<void>;
}

export default function useOfferBuilderMediaVisibility({
  offerId,
  mediaImages,
  removeUploadedImage,
  markDeletedOfferImage,
  markRoomImageRemoved,
  cleanupDeletedOfferGalleryImage,
  cleanupDeletedRoomGalleryImage,
  clearImageEdit,
  setRoomImagePendingEdit,
}: UseOfferBuilderMediaVisibilityParams): OfferBuilderMediaVisibilityController {
  const { enqueueSnackbar } = useSnackbar();
  const [hiddenImageOverrides, setHiddenImageOverrides] = useState<Record<string, boolean>>({});
  const [roomImagePendingDeletion, setRoomImagePendingDeletion] = useState<MediaImage | undefined>();
  const hiddenImageUpdatePublicIdsRef = useRef<Set<string>>(new Set());
  const roomImageDeletionPromisesRef = useRef<Map<string, Promise<void>>>(new Map());

  const deletePersistedRoomImage = (
    image: MediaImage,
    successMessage: string,
    errorMessage: string,
    showSuccess: boolean,
    showError: boolean,
  ) => {
    if (!canPersistRoomImageHiddenState(image)) {
      return;
    }

    const imageSourceKey = getMediaImageSourceKey(image);
    const existingDeletionPromise = roomImageDeletionPromisesRef.current.get(imageSourceKey);

    if (existingDeletionPromise) {
      return existingDeletionPromise;
    }

    const deletionPromise = ReservationService.deleteRoomTypeImage(
      image.sourceImageId,
      image.sourcePropertyId,
      image.sourceRoomTypeId,
    )
      .then(() => {
        if (showSuccess) {
          enqueueSnackbar(successMessage, { variant: 'success' });
        }
      })
      .catch((error) => {
        if (error instanceof Error) {
          reportError(error);
        }
        if (showError) {
          enqueueSnackbar(getErrorMessage(error, errorMessage), { variant: 'error' });
        }
        throw error;
      })
      .finally(() => {
        roomImageDeletionPromisesRef.current.delete(imageSourceKey);
      });

    roomImageDeletionPromisesRef.current.set(imageSourceKey, deletionPromise);

    return deletionPromise;
  };

  const removeImageFromRoom = async (image: MediaImage, options?: { showError?: boolean; showSuccess?: boolean }) => {
    const showSuccess = options?.showSuccess ?? true;
    const showError = options?.showError ?? true;
    const roomKey = image.sourceRoomId;

    const persistedRoomImageDeletion = deletePersistedRoomImage(
      image,
      'Room image removed',
      'Failed to remove room image',
      showSuccess,
      showError,
    );

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
    }

    removeUploadedImage(image.publicId);
    if (roomKey) {
      markRoomImageRemoved(roomKey, image.publicId);
      cleanupDeletedRoomGalleryImage(roomKey, image.publicId, false);
    }
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, image.publicId));
  };

  const deleteImage = async (publicId: string, options?: { showError?: boolean; showSuccess?: boolean }) => {
    const showSuccess = options?.showSuccess ?? true;
    const showError = options?.showError ?? true;
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    const persistedRoomImageDeletion = image
      ? deletePersistedRoomImage(image, 'Room image deleted', 'Failed to delete room image', showSuccess, showError)
      : undefined;

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
    }
    if (image && offerId && image.sourceImageId && !image.sourceRoomId) {
      try {
        await OffersService.deleteImage(offerId, image.sourceImageId);
        if (showSuccess) {
          enqueueSnackbar('Offer image deleted', { variant: 'success' });
        }
      } catch (error) {
        if (error instanceof Error) {
          reportError(error);
        }
        if (showError) {
          enqueueSnackbar(getErrorMessage(error, 'Failed to delete offer image'), { variant: 'error' });
        }
        throw error;
      }
    }

    if (image) {
      if (image.sourceType === 'offer') {
        removeUploadedImage(publicId);
        markDeletedOfferImage(publicId);
        cleanupDeletedOfferGalleryImage(publicId, false);
      } else if (image.sourceRoomId) {
        removeUploadedImage(publicId);
        markRoomImageRemoved(image.sourceRoomId, publicId);
        cleanupDeletedRoomGalleryImage(image.sourceRoomId, publicId, false);
      }
    }
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, publicId));
    clearImageEdit(publicId);
  };

  const toggleHiddenImage = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    const persistentImage = canPersistRoomImageHiddenState(image) ? image : undefined;

    if (persistentImage) {
      if (hiddenImageUpdatePublicIdsRef.current.has(publicId)) {
        return;
      }

      hiddenImageUpdatePublicIdsRef.current.add(publicId);
    }

    const previousHiddenOverride = hiddenImageOverrides[publicId];
    const nextHidden = !(previousHiddenOverride ?? image.hidden ?? false);

    setHiddenImageOverrides((currentOverrides) => ({
      ...currentOverrides,
      [publicId]: nextHidden,
    }));

    if (!persistentImage) {
      return;
    }

    try {
      await ReservationService.updateRoomTypeImage(
        {
          id_cloudinary_external: persistentImage.publicId,
          hidden: nextHidden,
        },
        persistentImage.sourceImageId,
        persistentImage.sourcePropertyId,
        persistentImage.sourceRoomTypeId,
      );
      enqueueSnackbar(nextHidden ? 'Room image hidden' : 'Room image shown', { variant: 'success' });
    } catch (error) {
      setHiddenImageOverrides((currentOverrides) =>
        previousHiddenOverride === undefined
          ? removeImageOverride(currentOverrides, publicId)
          : { ...currentOverrides, [publicId]: previousHiddenOverride },
      );
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update room image visibility'), { variant: 'error' });
    } finally {
      hiddenImageUpdatePublicIdsRef.current.delete(publicId);
    }
  };

  const removeRoomImageFromEditor = (image: MediaImage) => {
    setRoomImagePendingDeletion(image);
  };

  const closeRoomImageDeleteDialog = () => {
    setRoomImagePendingDeletion(undefined);
  };

  const confirmRemoveRoomImage = async () => {
    if (!roomImagePendingDeletion) {
      return;
    }

    try {
      await removeImageFromRoom(roomImagePendingDeletion);
    } catch {
      return;
    }

    setRoomImagePendingEdit((currentImage) =>
      currentImage?.publicId === roomImagePendingDeletion.publicId ? undefined : currentImage,
    );
    setRoomImagePendingDeletion(undefined);
  };

  return {
    hiddenImageOverrides,
    setHiddenImageOverrides,
    roomImagePendingDeletion,
    removeRoomImageFromEditor,
    closeRoomImageDeleteDialog,
    confirmRemoveRoomImage,
    deleteImage,
    removeImageFromRoom,
    toggleHiddenImage,
  };
}
